import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { ReactComponent as BlackStarsIcon } from 'assets/icons/BlackStars.svg';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';
import { theme } from 'config/theme';
import ArticlesSummarizedResults from './ArticlesSummarizedResults';
import Overlay from 'components/common/overlay/Overlay';
import useUserData from 'hooks/useUserData';
import {
  CONFIGS,
  getCustomFieldsDefaultValues,
  getCustomFieldsValidationSchema,
  useConfigQuery,
} from '@buildwise/libs/configurations';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { ConfigEntryFieldType } from 'config/config';
import { FieldWrapper } from '@buildwise/libs/people';
import RenderFilterFieldByType from '@buildwise/libs/feed/components/RenderFilterFieldByType';
import { getDefaultFilterValueByType } from '@buildwise/libs/feed/helpers';
import { HomeModuleFeatures, useBuildWiseModules } from '@buildwise/libs/helpers';
import useFetchEventSource from '@buildwise/pages/search/hooks/useFetchEventSource';
import { GEN_AI_SERVICE_ENDPOINTS, GEN_AI_SERVICE_URL } from '@buildwise/libs/services';
import HandleOperation from 'components/utils/HandleOperation';

type SummarizeArticlesOverlayProps = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

export type FilterType = { filterName: string; filterValues: string[] }[];

function SummarizeArticlesOverlay({ open, setOpen }: SummarizeArticlesOverlayProps) {
  const { companyId } = useUserData();
  const {
    activeUniqueEntries,
    isLoading: isLoadingConfig,
    isError: isConfigError,
  } = useConfigQuery({
    companyId,
    configName: CONFIGS.LEARNINGS_SUMMARY_FILTERS.configName,
  });
  const filtersForm = useForm<Record<string, any>>({
    resolver: yupResolver(
      Yup.object().shape(
        getCustomFieldsValidationSchema({
          fields: activeUniqueEntries,
          doNotAddRequiredValidation: true,
        }),
      ),
    ),
    defaultValues: getCustomFieldsDefaultValues({
      fields: activeUniqueEntries,
    }),
  });
  const {
    formState: { isSubmitting, isDirty, isValid, dirtyFields },
    handleSubmit,
    reset,
  } = filtersForm;
  const { homeModuleFeatures } = useBuildWiseModules();
  const {
    fetchSSE,
    response: { loading, isStreaming, genAIResponse, error, sourceRefsMetaData },
    reset: resetSummaryState,
  } = useFetchEventSource();

  useEffect(() => {
    if (activeUniqueEntries.length) {
      reset(
        getCustomFieldsDefaultValues({
          fields: activeUniqueEntries,
        }),
      );
    }
  }, [activeUniqueEntries]);

  const handleApply = useCallback(
    async (values: Record<string, any>) => {
      resetSummaryState();
      const filters = activeUniqueEntries
        .map(({ entryCode, referenceObject }) => {
          const defaultValue = getDefaultFilterValueByType(referenceObject?.type);
          const filterValue = values[entryCode] ?? defaultValue;
          const filterValues = Array.isArray(filterValue)
            ? filterValue
            : filterValue
              ? [filterValue]
              : [];

          return {
            filterName: entryCode,
            filterValues,
            ...(referenceObject?.type === ConfigEntryFieldType.CHECKBOX && {
              isChecked: values[entryCode],
            }),
          };
        })
        .filter(({ filterValues }) => filterValues.length > 0);

      const payload = {
        companyId,
        filters,
      };
      const _url = `${GEN_AI_SERVICE_URL}${GEN_AI_SERVICE_ENDPOINTS.GENERATE_COMPANY_ARTICLES_SUMMARY}`;
      await fetchSSE(_url, payload);
    },
    [activeUniqueEntries, companyId],
  );
  const onSummarize = handleSubmit(handleApply);

  return (
    <Overlay open={open} setOpen={setOpen}>
      <>
        <StickyContainer>
          <Box display="flex" alignItems="center" justifyContent="space-between" p={1}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography
                variant="h5"
                sx={(theme) => ({
                  color: theme.palette.custom.tuna,
                  fontSize: 16,
                  fontWeight: 700,
                  ml: 1,
                })}
              >
                {homeModuleFeatures[HomeModuleFeatures.SUMMARIZE_LEARNINGS].displayName}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => {
                setOpen(false);
              }}
            >
              <CloseLineIcon color={theme.palette.custom.paleSky} />
            </IconButton>
          </Box>
          <Divider />
        </StickyContainer>
        <Box px={2} pt={2} height="100%" overflow="auto">
          <Typography
            sx={(theme) => ({
              color: theme.palette.custom.tuna,
              fontSize: 16,
              mb: 2,
            })}
          >
            Select the attributes that you are interested in and BuildWise will summarize all of the
            learnings for you.
          </Typography>
          <>
            <HandleOperation
              isLoading={isLoadingConfig}
              isError={isConfigError}
              data={activeUniqueEntries}
              skelton={
                <>
                  <Box display="flex" alignItems="center" justifyContent="space-between" my={1}>
                    <Skeleton variant="text" width="20%" height={30} />
                    <Skeleton variant="text" width="70%" height={40} />
                  </Box>
                </>
              }
            >
              <FormProvider {...filtersForm}>
                {activeUniqueEntries.map((filter) => {
                  if (filter.referenceObject?.type === ConfigEntryFieldType.DATE) {
                    return null;
                  }

                  return (
                    <FieldWrapper
                      key={filter.id}
                      id={filter.entryCode}
                      label={filter.referenceObject?.label || filter.entryDescription}
                      disabled={isSubmitting || isStreaming}
                    >
                      <RenderFilterFieldByType field={filter} />
                    </FieldWrapper>
                  );
                })}
              </FormProvider>
            </HandleOperation>
            <Box display="flex" alignItems="center" justifyContent="flex-end" mt={1}>
              <Button
                variant="contained"
                onClick={onSummarize}
                startIcon={
                  isSubmitting || isStreaming ? null : <BlackStarsIcon width={18} height={18} />
                }
                disabled={
                  isSubmitting ||
                  isStreaming ||
                  !(isDirty || isValid) ||
                  !Object.keys(dirtyFields).length
                }
              >
                {isSubmitting || isStreaming ? (
                  <CircularProgress
                    sx={(theme) => ({ color: theme.palette.custom.black })}
                    size={20}
                  />
                ) : (
                  'Summarize'
                )}
              </Button>
            </Box>
          </>
          <Collapse in={genAIResponse || isStreaming || loading || error}>
            <ArticlesSummarizedResults
              isStreaming={isStreaming}
              loading={loading}
              genAIResponse={genAIResponse}
              sourceRefsMetaData={sourceRefsMetaData}
              error={error}
            />
          </Collapse>
        </Box>
      </>
    </Overlay>
  );
}

const StickyContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.custom.white,
  position: 'sticky',
  top: 0,
  zIndex: 1,
}));

export default SummarizeArticlesOverlay;
